import { cmpVersion } from './version';

/**
 * Client mirror of services/lib/domain/deviceService.ts.
 *
 * The server refuses a capture from a Device Service below this, at both the precheck and the submission.
 * This copy exists only to say so BEFORE the operator has entered demographics and captured ten fingers —
 * it is an explanation, never the control. Keep the two in step; the server is the one that decides.
 */
export const MIN_DEVICE_SERVICE_VERSION = '2.0.2';

/** False only when we KNOW the version is too old — an unreadable version is not evidence. */
export function deviceServiceBelowMinimum(version: string | null | undefined): boolean {
  return belowVersion(version, MIN_DEVICE_SERVICE_VERSION);
}

/**
 * The build that added POST /captures/{id}/print, per Device Service branch. Unlike
 * MIN_DEVICE_SERVICE_VERSION these are not a floor the server enforces — they are one feature's
 * requirement, checked only to explain a failed print. Everything else still works without it.
 *
 * The Device Service now ships as TWO lines that do not share a version sequence: 2.0.3 was the last
 * 64-bit build before the split, 2.0.4 was 32-bit only, and the 32-bit line stays at 2.0.4.x from here
 * (x rising as functionality is added) while 64-bit continues 2.0.5, 2.0.6, … So there is no single
 * number to compare against: a 32-bit 2.0.4.1 HAS print while being numerically below the 64-bit floor,
 * and comparing it against 2.0.5 would tell a customer who is fully up to date to update.
 */
export const MIN_PRINT_VERSION_64 = '2.0.5';
export const MIN_PRINT_VERSION_32 = '2.0.4.1';

/**
 * The floor for the architecture the Device Service reports at /bits ("32" or "64"), or null when it did
 * not say — which is not the same as either branch, and callers must not present it as one.
 */
export function minPrintVersion(bits: string | null | undefined): string | null {
  const b = (bits ?? '').trim();
  return b === '64' ? MIN_PRINT_VERSION_64 : b === '32' ? MIN_PRINT_VERSION_32 : null;
}

/** True only when we KNOW this build predates print ON ITS OWN BRANCH. */
export function deviceServiceBelowPrint(version: string | null | undefined, bits: string | null | undefined): boolean {
  /*
   * An unknown architecture is compared against the LOWER floor. It is the only choice that cannot
   * accuse a build that does have print: no 64-bit build was ever numbered 2.0.4.x, so nothing real sits
   * between the two floors, and every 64-bit build that predates print (2.0.3 and earlier) is below both.
   */
  return belowVersion(version, minPrintVersion(bits) ?? MIN_PRINT_VERSION_32);
}

function belowVersion(version: string | null | undefined, min: string): boolean {
  const v = (version ?? '').trim();
  if (!v || !/\d/.test(v)) return false;
  return cmpVersion(v, min) < 0;
}
